import React, { JSX, useEffect, useState } from 'react';
import Constants from '../../../assets/utils/constant';
import axios from 'axios';
import Icons from 'components/atoms/Icons';
import Link from 'next/link';
import Image from 'next/image';
import { ProviderFields } from 'components/types/MapTypes';
import { ScheduleDay } from 'components/types/ScheduleTypes';
import { formatSearchResultUrl } from 'assets/utils/formatUrl';
import { myChart } from '../shared/MyChartConfig';

type ProfileImageProps = {
  id: string;
  imageUrl?: string;
  url?: string;
  fullName: string;
  gender: 'Male' | 'Female';
  videoUrl?: string;
};

const ProfileImage = (props: ProfileImageProps) => {
  const fallbackImage =
    props.gender === 'Male'
      ? Constants.fallbackImages.providerMale
      : Constants.fallbackImages.providerFemale;

  const [imageSrc, setImageSrc] = useState<string>(
    props.imageUrl || fallbackImage
  );

  useEffect(() => {
    setImageSrc(props.imageUrl || fallbackImage);
  }, [props.imageUrl, fallbackImage]);

  return (
    <>
      <div className="image-container">
        {/* <div className="image-bg">
            <Image
              src="/images/profile-bg.png"
              alt=""
              layout="fill"
              objectFit="cover"
            />
          </div> */}

        {/* <div className="image" key={props.id}> */}
        <Image
          itemProp="image"
          src={imageSrc}
          alt={props.fullName}
          layout="fill"
          objectFit="cover"
          onError={() => setImageSrc(fallbackImage)}
        />
        {/* </div> */}
      </div>

      {props.videoUrl && props.url && (
        <Link href={props.url?.concat('#provider-video')} scroll={false}>
          <div className="video-btn">
            <Icons id="icon-play-filled" className="icon icon-play-filled" />
          </div>
        </Link>
      )}
    </>
  );
};

type ProviderSpecialtiesProps = {
  specialty?: Array<string>;
};

const ProviderSpecialties = (props: ProviderSpecialtiesProps): JSX.Element => {
  const { specialty } = props;

  return (
    <>
      {specialty && specialty.length > 0 && (
        <div
          className="specialties"
          itemProp="medicalSpecialty"
          itemType="https://schema.org/medicalSpecialty"
        >
          <p itemProp="specialty" className="specialty">
            <span>Specialty: </span>
            {specialty
              .flatMap((item: string) =>
                item.split('|').map((s: string) => s.trim())
              )
              .filter((s: string) => s.length > 0)
              .join(', ')}
          </p>
        </div>
      )}
    </>
  );
};

type ProviderCFAsProps = {
  cfas?: Array<string>;
};

const ProviderCFAs = (props: ProviderCFAsProps): JSX.Element => {
  const { cfas } = props;
  return (
    <>
      {cfas && cfas.length > 0 && (
        <div className="cfas">
          <p className="cfa">
            <span>Clinical Area of Focus: </span>
            {cfas.map((item: string) => item).join(', ')}
          </p>
        </div>
      )}
    </>
  );
};

type ProviderLocation = {
  id: string;
  city: string;
  phone: string;
  locationId: string;
};

type ProviderLocationsProps = {
  locations: Array<ProviderLocation>;
  profileUrl: string;
};

const ProviderLocations = (props: ProviderLocationsProps) => {
  const { locations } = props;
  const cities = [
    ...new Set(locations.map((loc: ProviderLocation) => loc.city)),
  ];

  return (
    <>
      <div className="result-detail">
        <Icons id="pld-location-pin" />
        <p>
          Office Locations:
          {` `}
          {cities.map((city: string, idx: number) => {
            return (
              <>
                {` `}
                <p key={idx} className="city">
                  {city}
                  {cities.length > 1 && idx < cities.length - 1 && (
                    <span>,</span>
                  )}
                </p>
              </>
            );
          })}
        </p>
      </div>
    </>
  );
};

type ProviderContactInfoProps = {
  accepting: boolean;
  locations: Array<ProviderLocation>;
  phoneNumber?: string;
  url: string;
  appt: string;
};

const ProviderContactInfo = (props: ProviderContactInfoProps) => (
  <>
    {props.locations &&
      props.locations[0]?.city &&
      (props.locations[0]?.phone || props.phoneNumber) && (
        <>
          <div className="result-detail">
            <Icons id="pld-phone" />
            <p>
              {props.locations[0]?.city}:{' '}
              <a href={`tel:${props.phoneNumber || props.locations[0].phone}`}>
                {props.phoneNumber
                  ? props.phoneNumber.replace(
                      props.phoneNumber.substring(0, 4),
                      `(${props.phoneNumber.substring(0, 3)}) `
                    )
                  : props.locations[0].phone}
              </a>
            </p>
          </div>
        </>
      )}
    {(props.accepting || props.appt) && <div className="divider"></div>}
    {props.accepting && (
      <div className="result-detail">
        <Icons id="icon-checkmark-circle" />
        <div className="new-patient-text accepting">Accepting New Patients</div>
      </div>
    )}
    {props.appt && (
      <>
        <div className="result-detail">
          <Icons id="pld-schedule" />
          <p className="osf-link">Schedule Online</p>
        </div>
        <p className="next-appt">{props.appt}</p>
      </>
    )}
  </>
);

type ProviderResultProps = {
  keyProp: string;
  distance?: number;
  origin?: Object;
  provider: ProviderFields;
};

const ProviderResult = (props: ProviderResultProps) => {
  const [nextAppointment, setNextAppointment] = useState<string | null>(null);

  const eligibleSpecialties = [
    'Pediatrics',
    'Family Medicine',
    'Internal Medicine',
    'Preventive Medicine',
    'Family Nurse Practitioner',
    'Urgent Care',
  ];

  const hasEligibleSpecialty =
    props.provider.specialties?.some(spec => {
      const specialtyValue =
        typeof spec === 'string' ? spec : (spec as any)?.specialtyName || '';

      return eligibleSpecialties.some(eligible =>
        specialtyValue.toLowerCase().includes(eligible.toLowerCase())
      );
    }) ?? false;

  const getNextAppointment = async (providerId: string) => {
    const daysIncrements = 30;
    const totalDays = 180;

    const fetchAppointments = async (startDate: Date, endDate: Date) => {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      try {
        const response = await axios.post(
          `${myChart.epicApi}${myChart.getScheduleDays}`,
          {
            StartDate: formattedStartDate,
            EndDate: formattedEndDate,
            ProviderID: providerId,
            ProviderIDType: 'External',
            VisitTypeIDs: [
              { ID: '40045', Type: 'External' },
              { ID: '40059', Type: 'External' },
              { ID: '1003', Type: 'External' },
            ],
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Epic-Client-ID': myChart.epicClientID,
              Accept: 'application/json',
              Authorization: `Basic ${myChart.epicAccessToken}`,
            },
          }
        );
        return response.data.ScheduleDays || [];
      } catch (error) {
        console.error('Error fetching next appointment:', error);
        throw error;
      }
    };

    const today = new Date();

    for (let startDay = 0; startDay < totalDays; startDay += daysIncrements) {
      const currentStartDate = new Date(today);
      currentStartDate.setDate(today.getDate() + startDay);

      const currentEndDate = new Date(today);
      currentEndDate.setDate(
        today.getDate() + Math.min(startDay + daysIncrements, totalDays)
      );

      const scheduleDays: ScheduleDay[] = await fetchAppointments(
        currentStartDate,
        currentEndDate
      );

      const firstAvailableDay = scheduleDays.find(
        (day: ScheduleDay) => day.Slots && day.Slots.length > 0
      );

      if (firstAvailableDay) {
        return firstAvailableDay.Date;
      }
    }

    return null;
  };

  useEffect(() => {
    const fetchAppointments = async () => {
      if (props.provider.epicid && hasEligibleSpecialty) {
        try {
          const nextAppointmentDate = await getNextAppointment(
            props.provider.epicid
          );

          if (nextAppointmentDate) {
            const appointmentDate = new Date(`${nextAppointmentDate}T00:00:00`);

            const options = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            };

            const formattedDate = appointmentDate.toLocaleDateString(
              'en-US',
              options as Intl.DateTimeFormatOptions
            );

            setNextAppointment(
              `Next available new patient appointment: ${formattedDate}`
            );
          } else {
            setNextAppointment(null);
          }
        } catch (error) {
          console.error('Error fetching next appointment:', error);
          setNextAppointment(null);
        }
      }
    };

    fetchAppointments();
  }, [props.provider.epicid]);

  const {
    fullname,
    specialty,
    locations,
    acceptingnewpatients,
    gender,
    clinicalfocusareas,
    title,
    link,
    image,
  } = props.provider;

  const providerProfileUrl = formatSearchResultUrl(link);

  return (
    <div
      className={`search-result provider-result`}
      itemScope
      itemType="http://schema.org/Physician"
      key={props.keyProp}
      id={props.keyProp}
    >
      <a
        className="card-link"
        href={providerProfileUrl}
        aria-label={`Profile for ${props.provider.fullname}`}
      ></a>
      <ProfileImage
        id={props.provider.id}
        imageUrl={image}
        fullName={fullname}
        gender={gender}
      />
      <div className="result-info">
        <div>
          <h3 className="btn-tertiary result-name" itemProp="name">
            {title}
          </h3>
        </div>

        <div className="result-details">
          <ProviderSpecialties specialty={specialty} />
          <ProviderCFAs cfas={clinicalfocusareas} />
          {locations && locations.length > 0 ? (
            <ProviderLocations
              locations={locations}
              profileUrl={providerProfileUrl}
            />
          ) : null}

          <ProviderContactInfo
            locations={locations}
            url={providerProfileUrl}
            appt={nextAppointment as string}
            accepting={acceptingnewpatients}
          />
        </div>
      </div>
    </div>
  );
};

export default ProviderResult;
